import { useNavigate } from 'react-router-dom';
import navigateParams from '../../../utils/navigation/navigateParams';
import { useBillsContext } from '../../../utils/store/context/bills-context/BillsContext';
import { downloadBill } from '../../../services/BillService';
import { BillFileType } from '../../../models/Bills.interfaces';
import JSZip from 'jszip';
import { AxiosError, AxiosResponse } from 'axios';
import { handleParseBlob } from './functions';

const useBillsHook = () => {
    const navigate = useNavigate();
    const {
        billsToDownload,
        setDownloadError,
        setBillsWithError,
        setBillsDownloaded,
        setBillsZip,
        setIsDownloadCompleted,
    } = useBillsContext();

    const downloadPdf = (idBill: string, downloadRef: React.RefObject<HTMLAnchorElement>) => {
        downloadBill(idBill)
            .then((response: AxiosResponse) => {
                handleParseBlob(response.data, idBill, downloadRef);
            })
            .catch((error: AxiosError) => {
                setDownloadError(error);
                navigate('/bills-download/feedback');
            });
    };

    const sendToNative = (url: string, type: string, title: string, id?: string) => {
        const queryParams = { id };
        const storage = { pendingBills: true, id };
        const urlComposed = url + '?id=' + id;
        localStorage.setItem('storage', JSON.stringify(storage));
        if (navigateParams(type, url, queryParams, storage, title)) {
            id ? navigate(urlComposed) : navigate(`${url}`);
        }
    };

    const handleParseMultiplePdf = async (files: BillFileType[]) => {
        const zip = new JSZip();

        try {
            files.forEach((file) => {
                if (file.file) zip.file(`${file.id}.pdf`, file.file, { binary: true });
            });

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            setBillsZip(zipBlob);
        } catch (error) {
            console.log(error);
        }
    };

    const downloadMultiplePdf = async () => {
        try {
            const billsArray = Array.from(billsToDownload.values());
            const successfulFiles: BillFileType[] = [];

            const downloadPromises = billsArray.map(async (billId) => {
                try {
                    const response = await downloadBill(billId);

                    successfulFiles.push({ file: response.data, id: billId });

                    // @ts-expect-error
                    setBillsDownloaded((prevState) => [...prevState, billId]);
                } catch (error) {
                    // @ts-expect-error
                    setBillsWithError((prevState) => [...prevState, billId]);
                }
            });

            await Promise.all(downloadPromises);

            if (successfulFiles.length === 0) {
                throw new Error('No files were downloaded successfully.');
            }

            await handleParseMultiplePdf(successfulFiles);
            setIsDownloadCompleted(true);
        } catch (error) {
            setDownloadError(error as AxiosError);
            navigate('/bills-download/feedback');
        }
    };

    return { sendToNative, downloadMultiplePdf, downloadPdf };
};

export default useBillsHook;
